import { IconButton, Stack } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { CustomCellProps } from 'components/common/grid/';
import IconMi from 'components/common/icon/IconMi';
import { MetricChartCompareLineType } from 'components/metric/chart';
import { prepareFormComponentValues } from 'components/common/form/layout/control';
import { RawFormComponentType, FormDataAPIType } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import {
    MetricOverlaysContext,
    MetricOverlaysContextType,
} from 'components/metric/collaboration-controls/overlay-metrics/OverlayMetrics';
import { prepareFormElementProps } from 'components/common/form/formTools';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { FormElementWrapper } from 'components/common/form/element/FormElementWrapper';

function FormDashSelect({ elementProps }: { elementProps: FormDataAPIType }) {
    const selectData = prepareFormComponentValues([
        { value: 'line', label: 'Line' },
        { value: 'shortdash', label: 'Short dash' },
        { value: 'shortdashdot', label: 'Short dash dot' },
        { value: 'shortdashdotdot', label: 'Short dash dot dot' },
        { value: 'dot', label: 'Dot' },
        { value: 'dash', label: 'Dash' },
        { value: 'dashdot', label: 'Dash dot' },
        { value: 'longdash', label: 'Long dash' },
        { value: 'longdashdot', label: 'Long dash dot' },
        { value: 'longdashdotdot', label: 'Long dash dot dot' },
    ]);

    const selectProps = prepareFormElementProps({
        ...elementProps,
        ...{
            label: 'widthLabel',
            component: {
                component: 'FormSelect',
                name: 'dashStyle',
                label: 'Line type',
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={selectProps}>
            <ReactHookFormController componentValues={selectData} elementProps={selectProps} />
        </FormElementWrapper>
    );
}

function FormColorPicker({ elementProps }: { elementProps: FormDataAPIType }) {
    const colorPickerProps = prepareFormElementProps({
        ...elementProps,
        ...{
            label: 'widthLabel',
            component: {
                component: 'ColorPicker',
                name: 'color',
                label: 'Color',
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={colorPickerProps}>
            <ReactHookFormController elementProps={colorPickerProps} />
        </FormElementWrapper>
    );
}

export default function CompareLinesGridDataCell({ reloadGridData, data }: CustomCellProps) {
    const context = useContext<MetricOverlaysContextType | null>(MetricOverlaysContext);

    const lineData: MetricChartCompareLineType = {
        ...data,
        //@ts-ignore
        id: data.compare_line_id,
        //@ts-ignore
        dashStyle: data.dash_style_raw,
    } as unknown as MetricChartCompareLineType;

    const elementProps = useCustomSimplifiedForm({ dashStyle: lineData.dashStyle, color: lineData.color });

    const onDeleteClick = () => {
        if (!context?.onDelete) {
            return;
        }
        context.onDelete('compareLinesGrid', lineData.id);
    };

    const popupSettings: PopupSettings = {
        title: 'overlay_options',
    };

    const onEditClick = () => {
        setIsEdit(true);
    };

    const handleSaveForm = () => {
        if (!context?.onUpdate) {
            return;
        }
        const values = elementProps.form.hookFormGetValues();
        context?.onUpdate('compareLinesGrid', {
            id: lineData.id,
            color: values.color,
            dashStyle: values.dashStyle,
        });
        setIsEdit(false);
    };

    const [isEdit, setIsEdit] = useState<boolean>(false);

    return (
        <Stack direction={'row'} sx={{ height: 1, alignItems: 'center', justifyContent: 'end' }}>
            {isEdit && (
                <Popup
                    settings={popupSettings}
                    open={isEdit}
                    onHide={() => setIsEdit(false)}
                    onConfirm={() => handleSaveForm()}
                >
                    <Stack spacing={2}>
                        <FormDashSelect elementProps={elementProps} />
                        <FormColorPicker elementProps={elementProps} />
                    </Stack>
                </Popup>
            )}
            <IconButton aria-label="edit" size={'small'} onClick={onEditClick}>
                <IconMi icon="gear" fontSize={'16'} />
            </IconButton>
            <IconButton aria-label="delete" size={'small'} onClick={onDeleteClick}>
                <IconMi icon="delete" fontSize={'16'} />
            </IconButton>
        </Stack>
    );
}
